import { conversion } from 'country-configuration/currency'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { CONTENT_PLACEHOLDER } from 'my-purchases/constants/constants'
import type { FormattedPrices } from 'my-purchases/types/purchaseCommon'
import { useMemo } from 'react'

/**
 * Study cases:
 *
 * #1) If current country = purchase country, just convert the price and add additional prices (if any)
 *  - rawPrice = number that represents a price in HRK currency
 *  - purchaseCurrency = 'HRK'
 *  - mainCurrencyIso = 'HRK'
 *  Result: { price: '100 Kn', additionalPrices: ['34 €'] }
 *
 * #2) If purchase currency info is not found, we just return formatted price
 * - rawPrice = number that represents a price in USD currency
 * - purchaseCurrency = 'USD'
 * - mainCurrencyIso = 'EUR'
 * 	Result: { price: '100 $', additionalPrices: [] }
 *
 * #3) If purchase currency info is found as additional currency, we convert the price and add it as additional prices
 *  - rawPrice = number that represents a price in HRK currency
 *  - purchaseCurrency = 'HRK'
 *  - currentCurrencyIso = 'EUR'
 *  - additionalCurrencies: ["HRK"]
 *  Result: { price: '100 Kn', additionalPrices: ['34 €'] }
 */

export interface PriceToFormatProps {
	rawPrice: number | null
	formattedPrice: string | null
	purchaseCurrency: string
}
export const useFormatPrice = () => {
	const { country } = useMasterData()

	const formatPrice = useMemo(
		() =>
			({
				rawPrice,
				formattedPrice,
				purchaseCurrency,
			}: PriceToFormatProps): FormattedPrices => {
				const {
					currency: { iso: currentCurrencyIso },
					additionalCurrencies = [],
				} = country

				if (!rawPrice) {
					return { price: CONTENT_PLACEHOLDER, additionalPrices: [] }
				}

				// Scenario 1
				if (currentCurrencyIso === purchaseCurrency) {
					return conversion(rawPrice, country)
				}

				const originalCurrencyInfo = additionalCurrencies?.find(
					({ iso }) => iso === purchaseCurrency
				)

				// Scenario 2
				if (!originalCurrencyInfo) {
					return {
						price: formattedPrice ?? CONTENT_PLACEHOLDER,
						additionalPrices: [],
					}
				}

				// Scenario 3
				const mainCurrencyPrice = rawPrice / originalCurrencyInfo.factor
				const activeCurrencyPriceFormatted = conversion(
					mainCurrencyPrice,
					country
				)
				const originalPurchasePriceFormatted =
					originalCurrencyInfo.format.replace(
						'%@',
						rawPrice.toFixed(originalCurrencyInfo.decimals).toString()
					)

				// Format other currencies regarding to current currency factor
				const restAdditionalPricesFormatted = additionalCurrencies
					.filter(({ iso }) => iso !== purchaseCurrency)
					.map(
						({ factor, decimals: currencyDecimal, format: currencyFormat }) => {
							return currencyFormat.replace(
								'%@',
								(mainCurrencyPrice * factor).toFixed(currencyDecimal)
							)
						}
					)
				return {
					price: originalPurchasePriceFormatted,
					additionalPrices: [
						activeCurrencyPriceFormatted.price,
						...restAdditionalPricesFormatted,
					],
				}
			},
		[country]
	)

	return { formatPrice }
}
